import { useMemo, useState } from 'react';
import { OrbitLink } from '@/components/insights/OrbitLink';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useRefreshSaasCustomer, useSaasCustomer, useSetSaasMapping, type AppHealth, type SaasCustomerDetail, type Seat } from '@/api/backup';
import { useIsAdmin } from '@/api/hooks';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/PageHeader';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { SearchInput } from '@/components/ui/search-input';
import { CompanySearch, type CompanyOption } from '@/components/CompanySearch';
import { HealthBadge, SEAT_TYPE_LABEL, SeatStateBadge } from '@/components/backup/BackupKit';
import { fmtAge, fmtBytes } from '@/lib/rmm-helpers';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { ROUTES, cippTenantPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Backup → one SaaS Protection customer: per-app health over the last 7 days, coverage vs the
 * tenant's licensed M365 users (gaps, leavers, archived-but-billed), and the seat list.
 */

type Tab = 'coverage' | 'seats';

export default function BackupSaasCustomerPage() {
  const id = Number(useParams<{ id: string }>().id);
  const { data, isLoading, error, isFetching } = useSaasCustomer(Number.isFinite(id) ? id : undefined);
  const refresh = useRefreshSaasCustomer();
  const [tab, setTab] = useState<Tab>('coverage');
  const c = data?.customer;

  return (
    <DesktopOnly title="Backup is available on desktop" message="Open Cosmos on a larger screen to view backup health.">
      <div className="mx-auto w-full max-w-[1500px] px-4 py-4 md:px-6">
        <Link to={ROUTES.backup.saas} className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3 w-3" /> SaaS Protection
        </Link>

        {isLoading ? (
          <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : error ? (
          <div className="mt-4 text-sm text-destructive">Failed to load: {error instanceof Error ? error.message : 'Unknown error'}</div>
        ) : data && c ? (
          <>
            <PageHeader
              title={c.name}
              isFetching={isFetching || refresh.isPending}
              subtitle={<>
                {c.domain}.onmicrosoft.com · {c.retentionType === 'ICR' ? 'infinite retention' : c.retentionType === 'TBR' ? 'time-based retention' : c.retentionType}
                {c.fetchedAt && <> · read from Datto {fmtRelative(c.fetchedAt)}</>}
                {c.error && <span className="ml-1.5 text-destructive">· last refresh failed: {c.error}</span>}
              </>}
              actions={<>
                {c.seats && <HealthBadge health={c.health} />}
                {c.mapping.tenant && (
                  <Button variant="outline" size="sm" asChild><Link to={cippTenantPath(c.mapping.tenant.customerId)}>M365 tenant</Link></Button>
                )}
                <Button variant="ghost" size="sm" title="Re-read this customer from Datto now" disabled={refresh.isPending}
                  onClick={() => refresh.mutate(id, { onError: (e) => toast.error(e instanceof Error ? e.message : 'Refresh failed') })}>
                  <RefreshCw className={cn('h-3.5 w-3.5', refresh.isPending && 'animate-spin')} />
                </Button>
              </>}
            />

            <MappingBar data={data} />

            {!data.loaded ? (
              <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Backup detail is still loading from Datto…</div>
            ) : (
              <>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                  {data.apps.map((a) => <AppCard key={a.app} a={a} />)}
                </div>

                <div className="mt-5 flex items-center gap-1 rounded-lg bg-muted p-1" style={{ width: 'fit-content' }}>
                  {([
                    ['coverage', `Coverage vs M365${data.coverage ? ` · ${data.coverage.notProtected.length + data.coverage.leavers.length + (c.seats?.archivedBillable ?? 0)}` : ''}`],
                    ['seats', `Seats · ${data.seats.length}`],
                  ] as [Tab, string][]).map(([t, label]) => (
                    <button key={t} type="button" onClick={() => setTab(t)}
                      className={cn('rounded-md px-3 py-1 text-xs font-medium transition-colors',
                        tab === t ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>{label}</button>
                  ))}
                </div>

                {tab === 'coverage' ? <CoverageTab data={data} /> : <SeatsTab seats={data.seats} />}
              </>
            )}
          </>
        ) : null}
      </div>
    </DesktopOnly>
  );
}

// -------- Mapping --------

function MappingBar({ data }: { data: SaasCustomerDetail }) {
  const { data: isAdmin } = useIsAdmin();
  const setMapping = useSetSaasMapping();
  const [editing, setEditing] = useState(false);
  const [pick, setPick] = useState<CompanyOption | null>(null);
  const m = data.customer.mapping;
  const id = data.customer.saasCustomerId;

  const save = (value: Parameters<typeof setMapping.mutate>[0]['value']) =>
    setMapping.mutate({ id, value }, {
      onSuccess: () => { setEditing(false); toast.success('Mapping saved'); },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not save'),
    });

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm shadow-sm">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Autotask company</span>
      {editing ? (
        <>
          <CompanySearch value={pick} onChange={setPick} className="w-72" />
          <Button size="sm" disabled={!pick || setMapping.isPending} onClick={() => pick && save({ companyId: pick.id, companyName: pick.name })}>Save</Button>
          <Button size="sm" variant="outline" onClick={() => save({ ignored: true })}>Ignore customer</Button>
          {m.source === 'manual' || m.source === 'ignored'
            ? <Button size="sm" variant="ghost" onClick={() => save(null)}>Reset to automatic</Button> : null}
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
        </>
      ) : (
        <>
          {m.companyName ? <OrbitLink companyId={m.companyId} className="font-medium text-foreground">{m.companyName}</OrbitLink>
            : m.source === 'ignored' ? <span className="italic text-muted-foreground">ignored</span>
            : <span className="text-warning">Not mapped</span>}
          <span className="text-xs text-muted-foreground">
            {m.source === 'cipp' ? `via CIPP tenant ${m.tenant?.displayName ?? ''}`
              : m.source === 'manual' ? `set manually by ${m.mappedBy}`
              : m.source === 'none' ? (m.tenant ? 'CIPP tenant found but not mapped to a company (Admin → CIPP mapping)' : 'no matching CIPP tenant') : ''}
          </span>
          {isAdmin && <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs" onClick={() => setEditing(true)}>Change</Button>}
        </>
      )}
    </div>
  );
}

// -------- Apps --------

function AppCard({ a }: { a: AppHealth }) {
  const latest = a.latest;
  return (
    <section className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">{a.app}</h3>
        <HealthBadge health={a.health} />
      </div>
      <div className="mt-2 text-xs text-muted-foreground">
        {latest ? <>{latest.withBackup}/{latest.active} services backed up · {latest.perfect} perfect</> : 'No backup history'}
      </div>
      <div className="text-xs text-muted-foreground">
        {a.lastFullyProtected ? <>Fully protected {fmtAge(a.lastFullyProtected)} ago</> : <span className="text-warning">Not fully protected in 7+ days</span>}
        {a.usedBytes != null && <> · {fmtBytes(a.usedBytes)}</>}
      </div>
      {/* Last 7 days, oldest → today. Each cell carries its status in the tooltip; the label row names the days. */}
      <div className="mt-3 flex gap-1">
        {a.days.map((d) => (
          <div key={d.daysAgo} className="flex-1" title={`${d.daysAgo === 0 ? 'Last 24 h' : `${d.daysAgo}–${d.daysAgo + 1} days ago`}: ${d.status} · ${d.withBackup}/${d.active} backed up, ${d.perfect} perfect`}>
            <div className={cn('h-5 rounded-sm', d.health === 'healthy' ? 'bg-success' : d.health === 'warning' ? 'bg-warning' : d.health === 'failing' ? 'bg-destructive' : 'bg-muted')} />
            <div className="mt-0.5 text-center text-[9.5px] tabular-nums text-muted-foreground">{d.daysAgo === 0 ? 'now' : `-${d.daysAgo}d`}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// -------- Coverage --------

function CoverageTab({ data }: { data: SaasCustomerDetail }) {
  const cov = data.coverage;
  const archived = data.seats.filter((s) => s.state === 'Archived' && s.billable);
  return (
    <div className="mt-3 space-y-4">
      {!cov ? (
        <div className="rounded-lg border border-border bg-card px-4 py-6 text-sm text-muted-foreground shadow-sm">
          Coverage needs this customer's M365 tenant in CIPP{data.customer.mapping.tenant ? '' : ' — none matched'}. Seat-level data is still on the Seats tab.
        </div>
      ) : cov.error ? (
        <div className="rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-xs text-warning">CIPP's MFA report is unavailable for this tenant: {cov.error}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Fact label="Licensed users protected" value={`${cov.pct ?? '—'}%`} sub={`${cov.protectedMembers} of ${cov.members} enabled, licensed members`} tone={cov.notProtected.length ? 'warn' : 'good'} />
            <Fact label="Not protected" value={cov.notProtected.length} sub="licensed users with no active seat" tone={cov.notProtected.length ? 'warn' : 'good'} />
            <Fact label="Leavers still protected" value={cov.leavers.length} sub="seats for blocked / unlicensed users" tone={cov.leavers.length ? 'warn' : 'good'} />
          </div>
          <ListCard title="Licensed users not protected" empty="Every licensed user has a protected seat."
            note="Usually a new user after the last discovery, a licence cap, or a seat set to Unprotected.">
            {cov.notProtected.map((u) => (
              <tr key={u.id}>
                <td className="px-4 py-2 font-medium text-foreground">{u.displayName}{u.isAdmin && <span className="ml-1.5 text-[10.5px] text-muted-foreground">admin</span>}</td>
                <td className="px-4 py-2 text-xs text-muted-foreground">{u.upn}</td>
                <td className="px-4 py-2"><SeatStateBadge state={u.seatState} /></td>
              </tr>
            ))}
          </ListCard>
          <ListCard title="Leavers still protected" empty="No protected seats for blocked or unlicensed users."
            note="Billed user seats for accounts that are sign-in blocked or unlicensed in M365 — check before the month-end usage snapshot.">
            {cov.leavers.map((s) => (
              <tr key={s.remoteId}>
                <td className="px-4 py-2 font-medium text-foreground">{s.name}</td>
                <td className="px-4 py-2 text-xs text-muted-foreground">{s.mainId}</td>
                <td className="px-4 py-2 text-xs text-warning">{s.reason}</td>
              </tr>
            ))}
          </ListCard>
          <p className="text-[11px] text-muted-foreground">M365 data from CIPP's report {cov.asOf ? fmtRelative(cov.asOf) : ''}; backup seats from Datto {data.customer.fetchedAt ? fmtRelative(data.customer.fetchedAt) : ''}.</p>
        </>
      )}
      <ListCard title={`Archived & still billed · ${archived.length}`} empty="No archived seats are billed."
        note="Users deleted from M365 whose backups are retained — and billed. Often deliberate (a leaver's data kept for retention), so review, don't bulk-remove.">
        {archived.map((s) => (
          <tr key={s.remoteId}>
            <td className="px-4 py-2 font-medium text-foreground">{s.name}</td>
            <td className="px-4 py-2 text-xs text-muted-foreground">{s.mainId}</td>
            <td className="px-4 py-2 text-xs text-muted-foreground">{SEAT_TYPE_LABEL[s.type] ?? s.type} · added {s.dateAdded?.slice(0, 10) ?? '—'}</td>
          </tr>
        ))}
      </ListCard>
    </div>
  );
}

function Fact({ label, value, sub, tone }: { label: string; value: string | number; sub: string; tone: 'good' | 'warn' }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn('mt-1 text-2xl font-semibold tabular-nums', tone === 'warn' ? 'text-amber-600 dark:text-amber-400' : 'text-foreground')}>{value}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>
    </div>
  );
}

function ListCard({ title, note, empty, children }: { title: string; note: string; empty: string; children: React.ReactNode[] }) {
  return (
    <section className="rounded-lg border border-border bg-card shadow-sm">
      <div className="border-b border-divider px-4 py-2.5">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
        <p className="mt-0.5 text-[11px] text-muted-foreground">{note}</p>
      </div>
      {children.length === 0 ? <div className="px-4 py-5 text-center text-xs text-muted-foreground">{empty}</div> : (
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-sm"><tbody className="divide-y divide-divider">{children}</tbody></table>
        </div>
      )}
    </section>
  );
}

// -------- Seats --------

function SeatsTab({ seats }: { seats: Seat[] }) {
  const [q, setQ] = useState('');
  const [state, setState] = useState('');
  const [type, setType] = useState('');
  const states = useMemo(() => [...new Set(seats.map((s) => s.state))].sort(), [seats]);
  const types = useMemo(() => [...new Set(seats.map((s) => s.type))].sort(), [seats]);
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return seats
      .filter((x) => (!state || x.state === state) && (!type || x.type === type) && (!s || x.name.toLowerCase().includes(s) || x.mainId.toLowerCase().includes(s)))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [seats, q, state, type]);
  const select = 'h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground';

  return (
    <div className="mt-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <SearchInput withIcon placeholder="Name or address" className="w-64" value={q} onValueChange={setQ} />
        <select value={type} onChange={(e) => setType(e.target.value)} className={cn(select, type && 'bg-secondary')}>
          <option value="">All types</option>
          {types.map((t) => <option key={t} value={t}>{SEAT_TYPE_LABEL[t] ?? t}</option>)}
        </select>
        <select value={state} onChange={(e) => setState(e.target.value)} className={cn(select, state && 'bg-secondary')}>
          <option value="">All states</option>
          {states.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <span className="text-xs text-muted-foreground">{rows.length} of {seats.length}</span>
      </div>
      <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Address / URL</th>
              <th className="px-4 py-2 font-medium">Type</th>
              <th className="px-4 py-2 font-medium">State</th>
              <th className="px-4 py-2 font-medium">Billable</th>
              <th className="px-4 py-2 font-medium">Added</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-divider">
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No seats match.</td></tr>}
            {rows.map((s) => (
              <tr key={s.remoteId} className="hover:bg-muted/30">
                <td className="max-w-[260px] truncate px-4 py-1.5 font-medium text-foreground" title={s.name}>{s.name}</td>
                <td className="max-w-[340px] truncate px-4 py-1.5 text-xs text-muted-foreground" title={s.mainId}>{s.mainId}</td>
                <td className="px-4 py-1.5 text-xs">{SEAT_TYPE_LABEL[s.type] ?? s.type}</td>
                <td className="px-4 py-1.5"><SeatStateBadge state={s.state} /></td>
                <td className="px-4 py-1.5 text-xs">{s.billable ? 'Yes' : <span className="text-muted-foreground">No</span>}</td>
                <td className="px-4 py-1.5 text-xs tabular-nums text-muted-foreground">{s.dateAdded?.slice(0, 10) ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

